import * as React from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import {
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";
import { FormBlock, FormSection } from "@/shared/foundation/primitives";

import {
  type KickoffSegment,
  substituteVariables,
  valuesWithDefaults,
} from "../kickoffPreview";
import { type LaunchFormValues } from "../launchFormSchema";
import { VariableRow } from "../VariableRow";

import styles from "./sections.module.css";

export interface VariablesSectionProps {
  variables: readonly TemplateVariable[];
  /** The message the agent starts on, placeholders and all; `""` for none. */
  kickoffMessage: string;
}

/** The dataset variables first — what the agent runs on — then the rest as declared. */
function datasetsFirst(
  variables: readonly TemplateVariable[],
): TemplateVariable[] {
  return [
    ...variables.filter(
      (variable) => variable.type === TemplateVariableType.DATASET_ID,
    ),
    ...variables.filter(
      (variable) => variable.type !== TemplateVariableType.DATASET_ID,
    ),
  ];
}

/** Whether the launch will hand the agent something for this variable. */
function isFilled(variable: TemplateVariable, value: string | undefined) {
  const hasDefault =
    variable.default !== undefined && variable.default !== null;
  return (value ?? "").trim() !== "" || hasDefault;
}

/**
 * What the agent is handed when it starts: one row per variable it declares,
 * and the kickoff message as those values make it.
 */
export function VariablesSection({
  variables,
  kickoffMessage,
}: VariablesSectionProps) {
  const { control, getFieldState } = useFormContext<LaunchFormValues>();
  const formState = useFormState({ control });
  const values = useWatch({ control, name: "values" });
  const filled = variables.filter((variable) =>
    isFilled(variable, values[variable.name]),
  ).length;

  return (
    <FormSection
      title="What variables the agent starts with"
      hint="This agent asks for these every time it runs, starting with what it runs on. They're dropped into its kickoff message and goals."
      actions={
        variables.length > 0 ? (
          <span
            className={styles.count}
            data-testid="launch-variables-count"
            data-filled={filled}
            data-total={variables.length}
          >
            {filled} of {variables.length} filled
          </span>
        ) : undefined
      }
    >
      {variables.length === 0 ? (
        <p className={styles.muted} data-testid="launch-no-variables">
          This agent has no declared variables. Launch it as it is.
        </p>
      ) : (
        <div
          className={styles.group}
          data-testid="launch-variables"
          data-count={variables.length}
        >
          {datasetsFirst(variables).map((variable) => (
            <VariableRow
              key={variable.name}
              variable={variable}
              error={
                getFieldState(`values.${variable.name}`, formState).error
                  ?.message
              }
            />
          ))}
        </div>
      )}
      {kickoffMessage !== "" && (
        <KickoffPreview
          segments={substituteVariables(
            kickoffMessage,
            valuesWithDefaults(variables, values),
          )}
        />
      )}
    </FormSection>
  );
}

function KickoffPreview({ segments }: { segments: KickoffSegment[] }) {
  const titleId = React.useId();
  return (
    <FormBlock titleId={titleId} title="Kickoff message it will receive">
      <blockquote
        className={styles.kickoff}
        data-testid="launch-kickoff-preview"
      >
        {segments.map((segment, index) => {
          switch (segment.kind) {
            case "filled":
              return <mark key={index}>{segment.text}</mark>;
            case "empty":
              return <code key={index}>{segment.text}</code>;
            case "text":
              return (
                <React.Fragment key={index}>{segment.text}</React.Fragment>
              );
          }
        })}
      </blockquote>
    </FormBlock>
  );
}
